import {useCallback} from "react";
import {useMessages} from "i18n";
import {arrivalReturnPath, focusLandmarkPath} from "components/sheetOpener";
import {ReferralGiftRefusedNotice} from "./ReferralGiftRefusedNotice";
import {ReferralOfferDialog} from "./ReferralOfferDialog";
import {useReferralClaim} from "./useReferralClaim";
import {useAnonymousReferralClaim, useReferralLandingOffer} from "./useReferralLandingOffer";
import type {ReferralAttributionOutcome} from "./useReferralAttribution";

/**
 * The gift a visitor meets on the referral link itself, and the claim that follows their signup
 * (kitecorp/transferify-ui#2020, owner 2026-09-05).
 *
 * <b>On the app SHELL, not on the landing page.</b> Both halves need it there. A share link is
 * `<origin>/?ref=CODE` today but the capture beside this one has always been mounted app-wide because a
 * link can point at any public route; and the sign-in this dialog raises closes in place, so the claim
 * that resumes afterwards has to be mounted wherever the reader happens to be standing.
 *
 * <b>Two adapters, one picker.</b> The dialog is the same component the account card, the create panel
 * and both pricing hosts open. Which of the two claim adapters it is handed is the whole difference
 * between the anonymous press (remember the tier, raise the sign-in) and the signed-in one (mint a
 * Stripe session and mount the sheet). Both hooks are called on every render because they are hooks;
 * only one of them is wired to a button.
 *
 * <b>It owns its own focus restore, unlike the picker's four other mounts.</b> Those are opened from a
 * durable control and MUI's trap restores to it. This one is opened by the ARRIVAL, so the trap records
 * no node to restore and skips its restore entirely, which parks a keyboard reader on the document body
 * when the dialog closes. `arrivalReturnPath` is the destination that replaces the opener there.
 *
 * Presentational: every rule is in `useReferralLandingOffer` and the React-free modules under it, and
 * the focus rule is `components/sheetOpener`'s.
 */
export function ReferralLandingPicker({attribution}: {attribution: ReferralAttributionOutcome}) {
	const {referralOffer} = useMessages();
	const landing = useReferralLandingOffer(attribution);
	const accountClaim = useReferralClaim();
	const anonymousClaim = useAnonymousReferralClaim(landing.choose);
	// Wired to the transition's own exit, which MUI runs AFTER the page behind the overlay is
	// un-hidden, so focus never lands in an `aria-hidden` tree.
	const restoreFocus = useCallback(() => {
		focusLandmarkPath(arrivalReturnPath(document));
	}, []);

	return (
		<>
			{landing.model !== null && (
				<ReferralOfferDialog
					open={landing.pickerOpen}
					model={landing.model}
					onClose={landing.close}
					onPaid={landing.closeAfterPaid}
					claim={landing.anonymous ? anonymousClaim : accountClaim}
					initialVariant={landing.initialVariant}
					continueLabel={landing.anonymous ? referralOffer.signupCta : undefined}
					onTransitionExited={restoreFocus}
				/>
			)}
			<ReferralGiftRefusedNotice
				open={landing.noticeOpen}
				onClose={landing.closeNotice}
				onTransitionExited={restoreFocus}
			/>
		</>
	);
}
